import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { BarChart2, AlertCircle } from 'lucide-react';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для отображения графика изменения стоимости с учетом инфляции
 */
const InflationImpactChart = ({ results, formData }) => {
  if (!results || !results.timeSeriesData || results.timeSeriesData.length === 0) {
    return null;
  }

  // Настраиваем данные для графика
  const chartData = results.timeSeriesData.map(item => ({
    year: item.year,
    value: item.value,
    inflationRate: item.inflationRate,
    cumulativeInflation: item.cumulativeInflation
  }));

  // Определяем цвет линии графика в зависимости от типа расчета
  const lineColor = results.calculationType === 'appreciation' ? '#10b981' : '#ef4444';
  const areaColor = results.calculationType === 'appreciation' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)';
  
  // Формат подписей значений
  const valueTickFormatter = (value) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)} млн`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(0)} тыс`;
    }
    return value.toFixed(0);
  };

  // Кастомный тултип
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 rounded shadow-lg">
          <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">{label} год</p>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Стоимость: <span className="font-medium">{formatCurrency(payload[0].value)}</span>
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Инфляция за год: <span className="font-medium">{formatNumber(payload[0].payload.inflationRate, 1)}%</span>
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Накопленная инфляция: <span className="font-medium">{formatNumber(payload[0].payload.cumulativeInflation, 1)}%</span>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart2 className="w-5 h-5 mr-2 text-blue-500" />
          Динамика изменения {results.calculationType === 'appreciation' ? 'стоимости' : 'покупательной способности'}
        </h3>
      </div>

      {/* Информационная подсказка */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded p-3 mb-4 flex items-start">
        <AlertCircle className="text-blue-500 dark:text-blue-400 w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700 dark:text-blue-300">
          <p>График показывает, как меняется {results.calculationType === 'appreciation' ? 'стоимость с учетом инфляции' : 'покупательная способность'} за выбранный период. 
          {results.calculationType === 'appreciation' 
            ? ' Рост кривой отражает удорожание из-за инфляции.' 
            : ' Снижение кривой отражает обесценивание из-за инфляции.'}
          </p>
        </div>
      </div>

      {/* График */}
      <div className="w-full h-72 mt-4">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="year" 
              domain={['dataMin', 'dataMax']}
              type="number"
              tickFormatter={(year) => year.toString()}
            />
            <YAxis
              tickFormatter={valueTickFormatter}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line
              type="monotone"
              dataKey="value"
              name={results.calculationType === 'appreciation' ? 'Стоимость с учетом инфляции' : 'Покупательная способность'}
              stroke={lineColor}
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              fill={areaColor}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Итоговое резюме */}
      <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700/30 rounded border border-gray-200 dark:border-gray-700">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          За период с {results.startYear} по {results.endYear} год 
          {results.calculationType === 'appreciation' 
            ? ` сумма ${formatCurrency(results.initialAmount)} выросла до ${formatCurrency(results.finalValue)} (на ${formatNumber(results.changePercent, 1)}%)` 
            : ` покупательная способность суммы ${formatCurrency(results.initialAmount)} снизилась до ${formatCurrency(results.finalValue)} (на ${formatNumber(Math.abs(results.changePercent), 1)}%)`
          }.
          Накопленная инфляция за этот период составила {formatNumber(results.cumulativeInflation, 1)}%.
        </p>
      </div>
    </div>
  );
};

export default InflationImpactChart;